import { Image, PenTool, FileText, ArrowLeft, Network, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";

interface CapabilityCard {
  icon: React.ReactNode;
  title: string;
  description: string;
  accentColor: string;
  borderHover: string;
}

const CAPABILITIES: CapabilityCard[] = [
  {
    icon: <Image className="w-4 h-4" />,
    title: "Diagram from Images",
    description: "Upload images to generate editable diagrams",
    accentColor: "text-blue-400",
    borderHover: "hover:border-blue-500/50",
  },
  {
    icon: <PenTool className="w-4 h-4" />,
    title: "Diagram Editing",
    description: "Add layers, shapes via chat commands",
    accentColor: "text-emerald-400",
    borderHover: "hover:border-emerald-500/50",
  },
  {
    icon: <FileText className="w-4 h-4" />,
    title: "Architecture Ontology",
    description: "Generate ontology from documents",
    accentColor: "text-violet-400",
    borderHover: "hover:border-violet-500/50",
  },
  {
    icon: <Network className="w-4 h-4" />,
    title: "Functional Mapping",
    description: "View component-requirement connections",
    accentColor: "text-amber-400",
    borderHover: "hover:border-amber-500/50",
  },
];

const EXAMPLE_QUERIES = [
  "Add a 4x3 layer",
  "Add a microservice",
  "Add a load balancer",
];

interface ArchitectureWelcomeScreenProps {
  onQuerySelect: (query: string) => void;
  onDismiss?: () => void;
  hasExistingMessages?: boolean;
}

export default function ArchitectureWelcomeScreen({
  onQuerySelect,
  onDismiss,
  hasExistingMessages = false,
}: ArchitectureWelcomeScreenProps) {
  return (
    <div className="flex flex-col items-center px-4 py-4 max-w-2xl mx-auto w-full">
      {/* Back button */}
      {hasExistingMessages && onDismiss && (
        <div className="w-full flex justify-end mb-2">
          <Button
            onClick={onDismiss}
            variant="ghost"
            size="sm"
            className="text-muted hover:text-white text-xs"
          >
            <ArrowLeft className="w-3 h-3 mr-1" />
            Back to Chat
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-4">
        <h1 className="text-lg font-semibold text-white mb-1">
          Architecture Agent
        </h1>
        <p className="text-sm text-muted">
          Create and manage architecture diagrams and ontologies
        </p>
      </div>

      {/* Capability Cards */}
      <div className="grid grid-cols-2 gap-3 w-full mb-4">
        {CAPABILITIES.map((capability, index) => (
          <div
            key={index}
            className={`p-3 rounded-lg border border-surface-border bg-surface-card/50 ${capability.borderHover} transition-all`}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className={capability.accentColor}>{capability.icon}</span>
              <span className={`text-sm font-medium ${capability.accentColor}`}>
                {capability.title}
              </span>
            </div>
            <p className="text-xs text-muted leading-relaxed">
              {capability.description}
            </p>
          </div>
        ))}
      </div>

      {/* Example Queries */}
      <div className="w-full">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Lightbulb className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs text-primary font-medium uppercase tracking-wide">
            Examples
          </span>
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          {EXAMPLE_QUERIES.map((query, index) => (
            <button
              key={index}
              onClick={() => onQuerySelect(query)}
              className="text-xs px-3 py-1.5 rounded-md border border-surface-border/60 text-muted hover:text-white hover:border-primary/30 transition-colors"
            >
              {query}
            </button>
          ))}
        </div>
      </div>

      {/* Hint */}
      <p className="mt-4 text-xs text-muted/60 text-center">
        Use <span className="text-primary font-medium">+</span> to attach files or generate diagrams
      </p>
    </div>
  );
}
